import { z } from 'zod';
import { objectIdSchema } from './common.js';

/**
 * LINE integration, phase 1: the host connects their own LINE Official Account
 * and Business Box sends booking messages from it.
 *
 * "Bring your own channels" rather than one central Business Box login, and not
 * by choice: a LINE user ID is scoped to the *provider*, so an ID obtained
 * through our Login channel could not be used to message anyone from the
 * host's Official Account. Each host therefore registers their own Login and
 * Messaging API channels under one provider of their own.
 */

/**
 * How hard to push the customer to add the Official Account as a friend.
 *
 * It matters more than it looks: a push only reaches a friend, so a shop that
 * leaves this off will see most of its bookings fall back to email. `required`
 * blocks booking in the MINI App until they add the account, which is the
 * setting that makes LINE messaging actually work — and the one to use
 * carefully, because it is also a step between a customer and a booking.
 */
export const LINE_FRIEND_MODES = ['off', 'suggest', 'required'] as const;
export const lineFriendModeSchema = z.enum(LINE_FRIEND_MODES);
export type LineFriendMode = z.infer<typeof lineFriendModeSchema>;

/** The booking messages phase 1 can send. Reminders and follow-ups are phase 3. */
export const LINE_MESSAGE_KINDS = ['confirmation', 'change', 'cancellation'] as const;
export const lineMessageKindSchema = z.enum(LINE_MESSAGE_KINDS);
export type LineMessageKind = z.infer<typeof lineMessageKindSchema>;

export const NOTIFICATION_CHANNELS = ['email', 'line'] as const;
export const notificationChannelSchema = z.enum(NOTIFICATION_CHANNELS);
export type NotificationChannel = z.infer<typeof notificationChannelSchema>;

/**
 * Why a LINE message was not sent. Recorded per booking so the host can answer
 * "did my customer get this?" without reading a log — and because three of
 * these are the customer's own doing and not a fault to investigate.
 */
export const LINE_FALLBACK_REASONS = [
  'not_linked',
  'not_friend',
  'blocked',
  'quota_exceeded',
  'connection_inactive',
  'send_failed',
] as const;
export const lineFallbackReasonSchema = z.enum(LINE_FALLBACK_REASONS);
export type LineFallbackReason = z.infer<typeof lineFallbackReasonSchema>;

// ---------------------------------------------------------------------------
// Connecting
// ---------------------------------------------------------------------------

/**
 * A channel secret is 32 hex characters and a channel ID is a 10-digit number.
 * Checked here so the wizard can say "that looks like the wrong box" before a
 * round trip to LINE, which is the single most common mistake in this setup.
 */
const channelIdSchema = z
  .string()
  .trim()
  .regex(/^\d{8,12}$/, 'a channel ID is a number, around 10 digits');
const channelSecretSchema = z
  .string()
  .trim()
  .regex(/^[0-9a-f]{32}$/i, 'a channel secret is 32 letters and numbers');

/**
 * A LIFF ID is `<10-digit channel id>-<8 chars>`, e.g. 1234567890-AbcdEfgh.
 * Checked here so the wizard can say "that is the channel ID, not the LIFF ID"
 * — they sit next to each other in LINE's console and are constantly confused.
 */
export const liffIdSchema = z
  .string()
  .trim()
  .regex(/^\d{8,12}-[0-9a-zA-Z]{8}$/, 'a LIFF ID looks like 1234567890-AbcdEfgh');

/** The Official Account's Messaging API channel. Complete or not sent. */
export const lineMessagingChannelInputSchema = z.object({
  channelId: channelIdSchema,
  channelSecret: channelSecretSchema,
});

/**
 * The booking screen's channel. All three together, because a secret without a
 * LIFF ID cannot start LIFF and a LIFF ID without a secret cannot have its ID
 * tokens verified — half a channel shows a tick over something that fails at
 * the customer.
 */
export const lineMiniAppChannelInputSchema = z.object({
  channelId: channelIdSchema,
  channelSecret: channelSecretSchema,
  liffId: liffIdSchema,
});

/** Preferences, which own no channel and therefore cannot disturb one. */
export const lineSettingsInputSchema = z.object({
  friendMode: lineFriendModeSchema.optional(),
  /** Which booking messages go by LINE when the customer is reachable there. */
  kinds: z.array(lineMessageKindSchema).optional(),
  /** Booking alerts pushed to the shop's own LINE. They cost quota; the UI says so. */
  ownerAlerts: z.boolean().optional(),
  /**
   * Off by default. A LINE channel has exactly one webhook, and if the shop
   * runs Lステップ, エルメ or Liny then *that* tool owns it — so nothing here
   * may depend on ours. Turning it on only adds the chat booking shortcut.
   */
  webhookEnabled: z.boolean().optional(),
});
export type LineSettingsInput = z.infer<typeof lineSettingsInputSchema>;

/** Secrets never appear here; only whether one is held. */
export const lineConnectionSchema = z.object({
  id: z.string(),
  /** `needs_attention` mirrors the calendar's `needs_reauth`: it still exists, it stopped working. */
  status: z.enum(['active', 'needs_attention']),
  lastError: z.string().nullable(),
  lastHealthCheckAt: z.string().datetime().nullable(),
  friendMode: lineFriendModeSchema,
  kinds: z.array(lineMessageKindSchema),
  ownerAlerts: z.boolean(),
  webhookEnabled: z.boolean(),
  /** Set once the shop opens the MINI App as themselves, in wizard step 6. */
  ownerLineUserId: z.string().nullable(),
  setupBy: z.enum(['owner', 'support']),
  /**
   * Null until the shop has an Official Account. Booking inside LINE needs only
   * the MINI App, so a connection can exist and take bookings while an OA's
   * phone verification is still outstanding — their confirmations go by email
   * until this is filled in.
   */
  messaging: z
    .object({
      channelId: z.string(),
      botUserId: z.string().nullable(),
      /** The @-handle customers search for, e.g. "@123abcde". */
      basicId: z.string().nullable(),
      displayName: z.string().nullable(),
      pictureUrl: z.string().nullable(),
    })
    .nullable(),
  /** Null until the shop finishes step 5; until then there is no booking in LINE. */
  miniApp: z
    .object({
      channelId: z.string(),
      liffId: z.string(),
      /** Where the shop must point the channel's endpoint, shown in step 4. */
      endpointUrl: z.string(),
      /** The link that goes behind the rich menu's 予約する button. */
      bookingUrl: z.string(),
    })
    .nullable(),
  login: z
    .object({ channelId: z.string() })
    .nullable()
    .describe('null until the shop adds a Login channel, which is phase 3'),
  /** The URL the host pastes into the LINE console. Shown, never stored. */
  webhookUrl: z.string(),
  webhookVerifiedAt: z.string().datetime().nullable(),
  friendCount: z.number().int(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type LineConnection = z.infer<typeof lineConnectionSchema>;

/**
 * The Official Account's monthly message allowance.
 *
 * Shown because the free plan is 200 messages a month and a busy host will run
 * through it: every booking that sends a confirmation and a change is two. When
 * it runs out we fall back to email, and the host should find that out here
 * rather than from a customer.
 */
export const lineQuotaSchema = z.object({
  type: z.string(),
  /** null when the plan has no cap LINE will report. */
  limit: z.number().int().nullable(),
  used: z.number().int(),
  remaining: z.number().int().nullable(),
});
export type LineQuota = z.infer<typeof lineQuotaSchema>;

export const lineTestMessageInputSchema = z.object({
  /** A contact of this host's, from someone who added the Official Account. */
  lineUserId: z.string().trim().min(1).max(64),
});

export const lineContactSchema = z.object({
  lineUserId: z.string(),
  displayName: z.string().nullable(),
  pictureUrl: z.string().nullable(),
  /** Null when nothing has told us either way; see LineContactDoc. */
  isFriend: z.boolean().nullable(),
  updatedAt: z.string().datetime(),
});
export type LineContact = z.infer<typeof lineContactSchema>;

export const lineContactListSchema = z.object({ items: z.array(lineContactSchema) });

/**
 * One line of "what we sent and where" per booking, so the host can answer a
 * customer who says they heard nothing. `channel` is what was actually used,
 * and `fallbackReason` says why it was not LINE when it was not.
 */
export const bookingNotificationSchema = z.object({
  kind: z.string(),
  channel: notificationChannelSchema,
  status: z.enum(['sent', 'failed']),
  fallbackReason: lineFallbackReasonSchema.nullable(),
  at: z.string().datetime(),
});
export type BookingNotification = z.infer<typeof bookingNotificationSchema>;

export const lineIdParams = z.object({ id: objectIdSchema });

// ---------------------------------------------------------------------------
// Booking inside LINE (the MINI App)
// ---------------------------------------------------------------------------

/**
 * What the MINI App sends us to say who is using it.
 *
 * The ID token is LINE's, signed by LINE, and is verified against *this*
 * connection's MINI App channel — not merely decoded. A token minted for some
 * other channel is the shop having pasted a LIFF ID from the wrong place, and
 * saying so is the whole point of checking the audience.
 */
export const lineMiniAppSessionInputSchema = z.object({
  idToken: z.string().min(1).max(4096),
  /**
   * From the wizard's step 6: a one-time token the shop carried in the link
   * they opened. It proves the person now in the MINI App is the account
   * holder, which is how Business Box learns their own LINE user id — the id
   * owner alerts are pushed to, and the one the test card goes to.
   */
  setupToken: z.string().max(256).optional(),
  /**
   * From `liff.getFriendship()`. Advisory: the client says it, so it is stored
   * as a hint and never trusted to decide whether a push will work — a push
   * refusal is what actually settles that.
   */
  isFriend: z.boolean().optional(),
});

export const lineBookerSessionSchema = z.object({
  /** Short-lived, signed, and scoped to one shop. Sent with the booking. */
  token: z.string(),
  expiresAt: z.string().datetime(),
  displayName: z.string().nullable(),
  pictureUrl: z.string().nullable(),
  /** Remembered from a previous booking in LINE, so it is asked for once. */
  email: z.string().nullable(),
  isFriend: z.boolean(),
  friendMode: lineFriendModeSchema,
  /** Where to send them to add the account, when friendMode requires it. */
  addFriendUrl: z.string().nullable(),
  shopName: z.string(),
  /**
   * The shop's handle. With it the MINI App calls the ordinary public booking
   * endpoints, so there is one booking implementation rather than a second one
   * for LINE that drifts from it.
   */
  username: z.string().nullable(),
});
export type LineBookerSession = z.infer<typeof lineBookerSessionSchema>;

/** What the MINI App needs before it can call liff.init. Public, no token. */
export const lineAppConfigSchema = z.object({
  liffId: z.string(),
  shopName: z.string(),
  friendMode: lineFriendModeSchema,
  addFriendUrl: z.string().nullable(),
});

/**
 * A booking as 「予約の確認・変更」 shows it.
 *
 * Deliberately not the host DTO and not the public API's: this is a customer
 * looking at their own appointment in a chat window, so it carries what fits on
 * a phone and the one token that lets them act on it.
 */
export const lineBookingSchema = z.object({
  id: z.string(),
  eventName: z.string(),
  start: z.string().datetime(),
  end: z.string().datetime(),
  timezone: z.string(),
  location: z.string(),
  joinUrl: z.string().nullable(),
  status: z.string(),
  /** The same manage token the confirmation email carries, so one page serves both. */
  manageUrl: z.string().nullable(),
  canReschedule: z.boolean(),
  canCancel: z.boolean(),
});
export type LineBooking = z.infer<typeof lineBookingSchema>;

export const lineBookingListSchema = z.object({ items: z.array(lineBookingSchema) });

/** Step 6: the link the shop opens on their phone, and what it is waiting for. */
export const lineOwnerLinkSchema = z.object({
  /** miniapp.line.me, so it opens inside LINE rather than a browser. */
  url: z.string(),
  expiresAt: z.string().datetime(),
});

export const lineTestCardResultSchema = z.object({
  sent: z.boolean(),
  /**
   * Which specific thing was wrong, so the wizard can name it. `wrong_provider`
   * is the one worth catching: the MINI App told us a user id the Official
   * Account has never heard of, which happens when the two channels are in
   * different providers — the most common setup mistake, and invisible until
   * the first message fails to send.
   */
  problem: z
    .enum(['not_friend', 'wrong_provider', 'no_official_account', 'quota_exceeded', 'failed'])
    .nullable(),
  /** Where to send the shop to add their own account, when that is the problem. */
  addFriendUrl: z.string().nullable(),
});
